import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge/Badge';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar, { type FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchableSelect from '../../components/common/SearchableSelect/SearchableSelect';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import Tooltip from '../../components/common/Tooltip/Tooltip';
import { usePerformanceSupportDashboard } from '../../hooks/usePerformanceSupportDashboard';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import {
  PERFORMANCE_SUPPORT_REASON_LABELS,
  type PerformanceSupportReason,
} from '../../types/performanceSupport';
import { buildWhatsAppUrl } from '../../utils/earlyCheckin';
import { formatPerformanceSupportPhone } from '../../utils/performanceSupportPhone';
import { formatPerformanceSupportWait } from '../../utils/performanceSupportWait';
import type {
  PerformanceSupportDashboardQuery,
  PerformanceSupportResponse,
} from '../../utils/performanceSupportDashboard';
import studentListStyles from '../Students/StudentList/StudentList.module.css';
import dashboardStyles from './Dashboard.module.css';
import styles from './PerformanceSupportPanel.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const REASON_FILTER_VALUES = [
  'exercise_pain',
  'exercise_substitution',
  'video_correction',
  'questions',
  'workout_adjustment',
  'became_pregnant',
] as const satisfies readonly PerformanceSupportReason[];

type SupportReasonFilter = (typeof REASON_FILTER_VALUES)[number];

const REASON_BADGE_VARIANT: Record<
  SupportReasonFilter,
  'success' | 'warning' | 'danger' | 'info' | 'primary' | 'pregnant'
> = {
  exercise_pain: 'danger',
  exercise_substitution: 'warning',
  video_correction: 'info',
  questions: 'primary',
  workout_adjustment: 'success',
  became_pregnant: 'pregnant',
};

const RESPONSE_OPTIONS = [
  { value: 'none', label: 'Nenhum' },
  { value: 'no', label: 'Não' },
  { value: 'yes', label: 'Sim' },
] as const;

const isResponseChoice = (value: string): value is PerformanceSupportResponse => (
  value === 'none' || value === 'no' || value === 'yes'
);

const respondedSelectValue = (response: PerformanceSupportResponse | null): string => (
  response === 'yes' || response === 'no' ? response : ''
);

const YES_LEAVES_LIST_MESSAGE = 'Ao marcar Sim, no dia seguinte esse registro da aluna sai da lista.';

interface PendingResponse {
  supportId: number;
  response: 'yes' | 'no';
}

const HEADERS = ['Aluna', 'Professora', 'Tempo sem resposta', 'Contato', 'Suporte da performance', 'Respondido'];
const GRID_TEMPLATE = 'minmax(200px, 1.4fr) minmax(140px, 1fr) 150px 210px minmax(180px, 1.1fr) 140px';

const SUPPORT_FILTER_SCHEMA = {
  searchName: queryFilter.string(''),
  reasons: queryFilter.array<SupportReasonFilter>([], { allowed: REASON_FILTER_VALUES, param: 'reason' }),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const isSupportReasonFilter = (value: string): value is SupportReasonFilter => (
  (REASON_FILTER_VALUES as readonly string[]).includes(value)
);

const PerformanceSupportPanel: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, resetFilters } = useQueryFilters(
    SUPPORT_FILTER_SCHEMA,
    { pageKey: 'page' },
  );

  const query = useMemo<PerformanceSupportDashboardQuery>(() => ({
    searchName: filters.searchName,
    reasons: filters.reasons,
    page: filters.page,
    pageSize: filters.pageSize,
  }), [filters.page, filters.pageSize, filters.reasons, filters.searchName]);

  const { snapshot, loading, error, markResponded } = usePerformanceSupportDashboard(query);
  const [now, setNow] = useState(() => new Date());
  const [savingSupportId, setSavingSupportId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingResponse, setPendingResponse] = useState<PendingResponse | null>(null);
  const totalCount = snapshot?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const page = Math.min(filters.page, totalPages);
  const students = snapshot?.students ?? [];
  const hasActiveFilters = Boolean(filters.searchName.trim()) || filters.reasons.length > 0;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const persistResponse = (supportId: number, value: PerformanceSupportResponse) => {
    setSavingSupportId(supportId);
    setSaveError(null);
    void markResponded(supportId, value)
      .catch((caught: unknown) => {
        setSaveError(caught instanceof Error ? caught.message : 'Não foi possível registrar a resposta.');
      })
      .finally(() => {
        setSavingSupportId(null);
      });
  };

  const handleResponded = (supportId: number, current: PerformanceSupportResponse | null, value: string) => {
    if (!isResponseChoice(value) || value === current || savingSupportId != null) {
      return;
    }

    if (value === 'yes' || value === 'no') {
      setPendingResponse({ supportId, response: value });
      return;
    }

    setPendingResponse(null);
    persistResponse(supportId, value);
  };

  const confirmPendingResponse = () => {
    if (!pendingResponse || savingSupportId != null) {
      return;
    }

    const next = pendingResponse;
    setPendingResponse(null);
    persistResponse(next.supportId, next.response);
  };

  const reasonOptions = useMemo(() => REASON_FILTER_VALUES.map((value) => ({
    value,
    label: PERFORMANCE_SUPPORT_REASON_LABELS[value],
  })), []);

  const chips: FilterChipEntry[] = useMemo(() => filters.reasons.map((value) => ({
    filterKey: 'reason',
    value,
    label: PERFORMANCE_SUPPORT_REASON_LABELS[value],
    onRemove: () => setFilter(
      'reasons',
      filters.reasons.filter((item) => item !== value),
    ),
  })), [filters.reasons, setFilter]);

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="Alunas:" count={totalCount} />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Nome, telefone ou professora"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={filters.reasons}
            onChange={(values) => setFilter('reasons', values.filter(isSupportReasonFilter))}
            options={reasonOptions}
            placeholder="Suporte da performance"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhum suporte encontrado"
            clearButtonLabel="Limpar suporte"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={() => resetFilters()} />
        )}
      </FilterBar>

      <FilterChipsBar chips={chips} compact />

      {saveError && <p className={styles['save-error']}>{saveError}</p>}

      {loading && !snapshot && <Loading />}
      {!loading && error && <EmptyState message={error} showImage={false} />}
      {!loading && !error && snapshot && totalCount === 0 && (
        <EmptyState
          message={hasActiveFilters
            ? 'Nenhuma aluna encontrada com os filtros selecionados.'
            : 'Nenhuma aluna com suporte da performance.'}
          showImage={false}
        />
      )}
      {!error && snapshot && totalCount > 0 && (
        <>
          <DataTable
            headers={HEADERS}
            gridTemplate={GRID_TEMPLATE}
            lastColumnIsActions={false}
            borderedRows
            boldHeaders
            highlightTerm={filters.searchName}
          >
            {students.map((row) => {
              const phone = formatPerformanceSupportPhone(row.phoneCountryCode, row.phoneNumber);
              const whatsAppUrl = buildWhatsAppUrl(row.phoneCountryCode, row.phoneNumber);

              return (
                <DataTable.Row key={row.id}>
                  <div className={styles['student-cell']}>
                    <div className={styles['name-row']}>
                      <button
                        type="button"
                        className={styles['name-link']}
                        onClick={() => navigate(`/alunas/${row.studentId}/editar`)}
                      >
                        {row.studentName}
                      </button>
                      <CopyStudentNameButton studentName={row.studentName} />
                    </div>
                  </div>
                  <div>{row.coachName || 'Sem professora'}</div>
                  <div className={styles.wait}>
                    {formatPerformanceSupportWait(row.createdAt, row.respondedAt, now)}
                  </div>
                  <div className={styles.contact}>
                    {whatsAppUrl && (
                      <Tooltip content="Abrir conversa no WhatsApp">
                        <a
                          className={studentListStyles['btn-whatsapp']}
                          href={whatsAppUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Abrir conversa no WhatsApp"
                        >
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </a>
                      </Tooltip>
                    )}
                    <span>{phone || '—'}</span>
                  </div>
                  <div className={styles['reason-cell']}>
                    {row.reason !== 'none' && (
                      <Badge variant={REASON_BADGE_VARIANT[row.reason]}>
                        {PERFORMANCE_SUPPORT_REASON_LABELS[row.reason]}
                      </Badge>
                    )}
                  </div>
                  <div className={styles['reply-select']}>
                    <SearchableSelect
                      value={respondedSelectValue(row.response)}
                      onChange={(value) => handleResponded(row.id, row.response, value)}
                      options={[...RESPONSE_OPTIONS]}
                      placeholder="Selecione"
                      searchPlaceholder="Pesquisar"
                      allowEmpty={false}
                      className={styles['reply-select-trigger']}
                      disabled={savingSupportId === row.id}
                    />
                  </div>
                </DataTable.Row>
              );
            })}
          </DataTable>
          <ListingBottomRow
            pageSize={filters.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                setFilter('pageSize', value);
              }
            }}
            pagination={{
              page,
              pageSize: filters.pageSize,
              totalRecords: totalCount,
              totalPages,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            loading={loading}
            onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
          />
          <ConfirmModal
            open={pendingResponse !== null}
            title="Respondido"
            message={pendingResponse?.response === 'no'
              ? `Marcar como Não? ${YES_LEAVES_LIST_MESSAGE}`
              : YES_LEAVES_LIST_MESSAGE}
            confirmLabel="Confirmar"
            cancelLabel="Cancelar"
            variant="info"
            icon="support_agent"
            loading={savingSupportId != null}
            onConfirm={confirmPendingResponse}
            onCancel={() => {
              if (savingSupportId == null) {
                setPendingResponse(null);
              }
            }}
          />
        </>
      )}
    </section>
  );
};

export default PerformanceSupportPanel;
